import type { Clip, Pose } from './pose';

/**
 * Procedural humanoid animation. Conventions (joint-local, radians):
 *  - shoulder / thigh: negative X swings the limb forward, positive X backward;
 *    shoulderL positive Z (shoulderR negative Z) raises the arm sideways.
 *  - elbow: negative X bends the forearm forward; knee: positive X bends the shin back.
 *  - The sword continues the line of the right forearm.
 */

const TAU = Math.PI * 2;

export function idlePose(
  t: number,
  variant: 'sword' | 'staff' | 'spear' | 'basket' | 'none' = 'sword',
): Pose {
  const b = Math.sin(t * 1.7);
  const look = Math.sin(t * 0.37) * 0.12 + Math.sin(t * 0.13) * 0.06;
  const p: Pose = {
    joints: {
      hips: [0, 0, 0],
      spine: [0.03 + b * 0.008, 0, 0],
      chest: [-0.02 + b * 0.014, 0, 0],
      neck: [0.02, look * 0.4, 0],
      head: [0.02 - b * 0.01, look * 0.6, 0],
      shoulderL: [0.05, 0, 0.12 + b * 0.01],
      elbowL: [-0.22, 0, 0],
      handL: [0, 0, 0],
      shoulderR: [-0.34, 0, -0.14 - b * 0.01],
      elbowR: [-0.62, 0, 0],
      handR: [0.25, 0, 0],
      thighL: [-0.04, 0, 0.05],
      kneeL: [0.08, 0, 0],
      footL: [-0.04, 0, -0.05],
      thighR: [0.07, 0, -0.06],
      kneeR: [0.1, 0, 0],
      footR: [-0.15, 0, 0.06],
      skirt: [0, 0, 0],
      hairTail: [0.1, 0, Math.sin(t * 1.3) * 0.04],
    },
    rootY: b * 0.004,
  };
  if (variant === 'staff') {
    p.joints.shoulderR = [-0.45, 0, -0.18];
    p.joints.elbowR = [-0.9, 0, 0];
    p.joints.handR = [1.0, 0, 0];
    p.joints.spine = [0.12, 0, 0];
  } else if (variant === 'spear') {
    p.joints.shoulderR = [-0.25, 0, -0.2];
    p.joints.elbowR = [-1.35, 0, 0];
    p.joints.handR = [1.55, 0, 0];
  } else if (variant === 'basket') {
    p.joints.shoulderL = [-0.2, 0, 0.2];
    p.joints.elbowL = [-1.3, 0, 0];
    p.joints.handL = [0.2, 0, 0];
    p.joints.shoulderR = [0.05, 0, -0.1];
    p.joints.elbowR = [-0.2, 0, 0];
  } else if (variant === 'none') {
    p.joints.shoulderR = [0.05, 0, -0.12];
    p.joints.elbowR = [-0.22, 0, 0];
    p.joints.handR = [0, 0, 0];
  }
  return p;
}

/**
 * Locomotion cycle. `phase` advances with distance travelled (no foot sliding);
 * `run` 0 = walk, 1 = run, 2 = qinggong sprint (arms swept back, strong lean).
 */
export function locomotionPose(phase: number, run: number, armed: boolean): Pose {
  const f = phase * TAU;
  const s = Math.sin(f);
  const c = Math.cos(f);
  const walk = Math.max(0, 1 - run);
  const runW = Math.min(1, Math.max(0, run <= 1 ? run : 2 - run));
  const sprint = Math.max(0, run - 1);
  const thighAmp = 0.45 * walk + 0.75 * runW + 0.95 * sprint;
  const kneeAmp = 0.5 * walk + 1.1 * runW + 1.35 * sprint;
  const lean = 0.05 * walk + 0.2 * runW + 0.42 * sprint;
  const bob = 0.025 * walk + 0.05 * runW + 0.06 * sprint;
  const knee = (x: number): number => Math.max(0, Math.sin(x)) * kneeAmp + 0.08;
  const armAmp = 0.35 * walk + 0.7 * runW;
  const p: Pose = {
    joints: {
      hips: [0, s * 0.12 * (walk + runW), 0],
      spine: [lean, 0, 0],
      chest: [0.02, -s * 0.18 * (walk + runW), 0],
      neck: [-lean * 0.4, 0, 0],
      head: [-lean * 0.5, s * 0.05, 0],
      thighL: [-s * thighAmp - 0.05 * runW, 0, 0.03],
      kneeL: [knee(f + Math.PI * 0.55), 0, 0],
      footL: [-0.1 + c * 0.25 * (runW + sprint), 0, 0],
      thighR: [s * thighAmp - 0.05 * runW, 0, -0.03],
      kneeR: [knee(f - Math.PI * 0.45), 0, 0],
      footR: [-0.1 - c * 0.25 * (runW + sprint), 0, 0],
      skirt: [-lean * 0.6 - Math.abs(s) * 0.08 * (runW + sprint), 0, 0],
      hairTail: [0.4 * runW + 0.9 * sprint + Math.abs(s) * 0.15, 0, s * 0.1],
      shoulderL: [s * armAmp + 1.2 * sprint, 0, 0.12 + 0.35 * sprint],
      elbowL: [-0.3 - 0.6 * runW - 0.1 * sprint, 0, 0],
    },
    rootY: -Math.abs(c) * bob * 0.6 + bob * 0.3 - 0.04 * runW - 0.08 * sprint,
  };
  if (armed) {
    p.joints.shoulderR = [-0.34 - s * armAmp * 0.45 + 1.35 * sprint, 0, -0.16 - 0.35 * sprint];
    p.joints.elbowR = [-0.65 + 0.2 * runW + 0.35 * sprint, 0, 0];
    p.joints.handR = [0.25 - 0.2 * sprint, 0, 0];
  } else {
    p.joints.shoulderR = [-s * armAmp + 1.2 * sprint, 0, -0.12 - 0.35 * sprint];
    p.joints.elbowR = [-0.3 - 0.6 * runW, 0, 0];
  }
  return p;
}

// ---------------------------------------------------------------- one-shot technique clips

const STANCE: Pose['joints'] = {
  thighL: [-0.35, 0, 0.12],
  kneeL: [0.45, 0, 0],
  footL: [-0.1, 0, -0.1],
  thighR: [0.3, 0, -0.12],
  kneeR: [0.3, 0, 0],
  footR: [-0.3, 0, 0.1],
};

export const HUMANOID_CLIPS: Record<string, Clip> = {
  ATTACK_1: {
    duration: 0.38,
    keys: [
      {
        t: 0,
        pose: {
          joints: {
            ...STANCE,
            chest: [0, -0.45, 0],
            shoulderR: [-1.9, 0, -0.95],
            elbowR: [-0.5, 0, 0],
            handR: [0.4, 0, 0],
            shoulderL: [-0.3, 0, 0.3],
          },
          rootY: -0.03,
        },
      },
      {
        t: 0.4,
        pose: {
          joints: {
            ...STANCE,
            chest: [0.05, -0.55, 0],
            shoulderR: [-2.1, 0, -1.1],
            elbowR: [-0.4, 0, 0],
            handR: [0.5, 0, 0],
          },
          rootY: -0.04,
        },
      },
      {
        t: 0.62,
        pose: {
          joints: {
            ...STANCE,
            chest: [0.12, 0.55, 0],
            hips: [0, 0.2, 0],
            shoulderR: [-1.0, 0, 0.55],
            elbowR: [-0.15, 0, 0],
            handR: [0.1, 0, 0],
            shoulderL: [0.4, 0, 0.5],
          },
          rootY: -0.06,
          rootZ: 0.12,
        },
      },
      {
        t: 1,
        pose: {
          joints: { ...STANCE, chest: [0.05, 0.25, 0], shoulderR: [-0.6, 0, 0.1], elbowR: [-0.5, 0, 0] },
          rootY: -0.03,
          rootZ: 0.06,
        },
      },
    ],
  },
  ATTACK_2: {
    duration: 0.4,
    keys: [
      {
        t: 0,
        pose: {
          joints: {
            ...STANCE,
            chest: [0.05, 0.5, 0],
            shoulderR: [-1.3, 0, 0.75],
            elbowR: [-1.2, 0, 0],
            handR: [0.2, 0, 0],
          },
          rootY: -0.04,
        },
      },
      {
        t: 0.42,
        pose: {
          joints: { ...STANCE, chest: [0.08, 0.6, 0], shoulderR: [-1.5, 0, 0.9], elbowR: [-1.4, 0, 0] },
          rootY: -0.05,
        },
      },
      {
        t: 0.64,
        pose: {
          joints: {
            ...STANCE,
            chest: [0.1, -0.6, 0],
            hips: [0, -0.2, 0],
            shoulderR: [-1.55, 0, -1.25],
            elbowR: [-0.1, 0, 0],
            handR: [0.05, 0, 0],
            shoulderL: [0.3, 0, 0.6],
          },
          rootY: -0.05,
          rootZ: 0.14,
        },
      },
      {
        t: 1,
        pose: {
          joints: { ...STANCE, chest: [0.04, -0.2, 0], shoulderR: [-0.7, 0, -0.5], elbowR: [-0.4, 0, 0] },
          rootY: -0.03,
          rootZ: 0.07,
        },
      },
    ],
  },
  ATTACK_3: {
    duration: 0.56,
    keys: [
      {
        t: 0,
        pose: {
          joints: {
            ...STANCE,
            chest: [0, -0.5, 0],
            shoulderR: [-1.4, 0, -1.4],
            elbowR: [-0.1, 0, 0],
            handR: [0.2, 0, 0],
            shoulderL: [-0.4, 0, 0.9],
          },
          rootYaw: 0,
          rootY: -0.06,
        },
      },
      {
        t: 0.3,
        pose: {
          joints: {
            ...STANCE,
            chest: [0.05, -0.6, 0],
            shoulderR: [-1.55, 0, -1.5],
            elbowR: [-0.05, 0, 0],
            shoulderL: [-0.4, 0, 1.0],
          },
          rootYaw: 0.3,
          rootY: -0.1,
        },
      },
      {
        t: 0.5,
        pose: {
          joints: {
            ...STANCE,
            chest: [0.1, 0.2, 0],
            shoulderR: [-1.55, 0, -1.45],
            elbowR: [0, 0, 0],
            shoulderL: [-0.4, 0, 1.0],
          },
          rootYaw: -Math.PI,
          rootY: -0.12,
          rootZ: 0.15,
        },
      },
      {
        t: 0.72,
        pose: {
          joints: {
            ...STANCE,
            chest: [0.15, 0.4, 0],
            shoulderR: [-1.3, 0, 0.5],
            elbowR: [-0.1, 0, 0],
            shoulderL: [0.4, 0, 0.7],
          },
          rootYaw: -TAU,
          rootY: -0.1,
          rootZ: 0.25,
        },
      },
      {
        t: 1,
        pose: {
          joints: { ...STANCE, chest: [0.04, 0.1, 0], shoulderR: [-0.5, 0, -0.1], elbowR: [-0.5, 0, 0] },
          rootYaw: -TAU,
          rootY: -0.03,
          rootZ: 0.1,
        },
      },
    ],
  },
  CAST_THRUST: {
    duration: 0.64,
    keys: [
      {
        t: 0,
        pose: {
          joints: {
            ...STANCE,
            chest: [0.05, -0.5, 0],
            shoulderR: [-1.1, 0, -0.4],
            elbowR: [-1.8, 0, 0],
            handR: [0.9, 0, 0],
            shoulderL: [-1.2, 0, 0.3],
            elbowL: [-0.4, 0, 0],
          },
          rootY: -0.08,
        },
      },
      {
        t: 0.45,
        pose: {
          joints: {
            ...STANCE,
            thighL: [-0.6, 0, 0.1],
            kneeL: [0.9, 0, 0],
            thighR: [0.5, 0, -0.1],
            kneeR: [0.6, 0, 0],
            chest: [0.1, -0.7, 0],
            shoulderR: [-1.2, 0, -0.5],
            elbowR: [-2.0, 0, 0],
            handR: [1.1, 0, 0],
            shoulderL: [-1.4, 0, 0.4],
            elbowL: [-0.2, 0, 0],
          },
          rootY: -0.18,
          rootZ: -0.08,
        },
      },
      {
        t: 0.58,
        pose: {
          joints: {
            thighL: [-0.9, 0, 0.1],
            kneeL: [0.7, 0, 0],
            footL: [0.1, 0, 0],
            thighR: [0.75, 0, -0.1],
            kneeR: [0.2, 0, 0],
            footR: [-0.5, 0, 0],
            spine: [0.25, 0, 0],
            chest: [0.05, 0.35, 0],
            shoulderR: [-1.6, 0, 0.1],
            elbowR: [0, 0, 0],
            handR: [-1.45, 0, 0],
            shoulderL: [0.9, 0, 0.5],
            elbowL: [-0.2, 0, 0],
          },
          rootY: -0.2,
          rootZ: 0.65,
        },
      },
      {
        t: 0.8,
        pose: {
          joints: {
            thighL: [-0.8, 0, 0.1],
            kneeL: [0.6, 0, 0],
            thighR: [0.7, 0, -0.1],
            kneeR: [0.2, 0, 0],
            spine: [0.2, 0, 0],
            chest: [0.05, 0.3, 0],
            shoulderR: [-1.55, 0, 0.1],
            elbowR: [0, 0, 0],
            handR: [-1.4, 0, 0],
            shoulderL: [0.8, 0, 0.5],
          },
          rootY: -0.18,
          rootZ: 0.6,
        },
      },
      {
        t: 1,
        pose: {
          joints: { ...STANCE, chest: [0.02, 0, 0], shoulderR: [-0.5, 0, -0.1], elbowR: [-0.6, 0, 0] },
          rootY: -0.03,
          rootZ: 0.2,
        },
      },
    ],
  },
  CAST_SLASH_WAVE: {
    duration: 0.72,
    keys: [
      {
        t: 0,
        pose: {
          joints: {
            ...STANCE,
            spine: [-0.15, 0, 0],
            chest: [-0.1, -0.2, 0],
            shoulderR: [-2.9, 0, -0.3],
            elbowR: [-0.4, 0, 0],
            handR: [0.3, 0, 0],
            shoulderL: [-0.6, 0, 0.6],
          },
          rootY: -0.02,
        },
      },
      {
        t: 0.45,
        pose: {
          joints: {
            ...STANCE,
            spine: [-0.25, 0, 0],
            chest: [-0.15, -0.35, 0],
            shoulderR: [-3.1, 0, -0.45],
            elbowR: [-0.6, 0, 0],
            handR: [0.6, 0, 0],
            shoulderL: [-0.4, 0, 0.8],
          },
          rootY: -0.04,
        },
      },
      {
        t: 0.56,
        pose: {
          joints: {
            ...STANCE,
            thighL: [-0.55, 0, 0.12],
            kneeL: [0.7, 0, 0],
            spine: [0.35, 0, 0],
            chest: [0.15, 0.45, 0],
            shoulderR: [-0.7, 0, 0.6],
            elbowR: [-0.05, 0, 0],
            handR: [-0.2, 0, 0],
            shoulderL: [0.6, 0, 0.7],
          },
          rootY: -0.16,
          rootZ: 0.2,
        },
      },
      {
        t: 1,
        pose: {
          joints: {
            ...STANCE,
            spine: [0.1, 0, 0],
            chest: [0.02, 0.1, 0],
            shoulderR: [-0.5, 0, -0.1],
            elbowR: [-0.5, 0, 0],
          },
          rootY: -0.04,
          rootZ: 0.1,
        },
      },
    ],
  },
  CAST_FORMATION: {
    duration: 0.98,
    keys: [
      {
        t: 0,
        pose: {
          joints: {
            ...STANCE,
            shoulderL: [-1.2, 0, 0.6],
            elbowL: [-1.2, 0, 0],
            shoulderR: [-0.9, 0, -0.9],
            elbowR: [-0.8, 0, 0],
            chest: [0, 0.3, 0],
          },
          rootY: -0.06,
        },
      },
      {
        t: 0.22,
        pose: {
          joints: {
            ...STANCE,
            shoulderL: [-0.6, 0, 1.1],
            elbowL: [-1.4, 0, 0],
            shoulderR: [-1.5, 0, -0.3],
            elbowR: [-1.1, 0, 0],
            chest: [0, -0.3, 0],
          },
          rootY: -0.1,
        },
      },
      {
        t: 0.44,
        pose: {
          joints: {
            ...STANCE,
            shoulderL: [-1.5, 0, 0.3],
            elbowL: [-0.9, 0, 0],
            shoulderR: [-0.7, 0, -1.1],
            elbowR: [-1.3, 0, 0],
            chest: [0, 0.35, 0],
          },
          rootY: -0.1,
        },
      },
      {
        t: 0.56,
        pose: {
          joints: {
            ...STANCE,
            spine: [-0.1, 0, 0],
            shoulderL: [-2.6, 0, 0.3],
            elbowL: [-0.3, 0, 0],
            shoulderR: [-2.9, 0, -0.2],
            elbowR: [-0.2, 0, 0],
            handR: [3.0, 0, 0],
            chest: [-0.1, 0, 0],
          },
          rootY: 0.06,
        },
      },
      {
        t: 0.64,
        pose: {
          joints: {
            thighL: [-0.9, 0, 0.25],
            kneeL: [1.5, 0, 0],
            footL: [-0.5, 0, 0],
            thighR: [0.2, 0, -0.25],
            kneeR: [1.6, 0, 0],
            footR: [-0.8, 0, 0],
            spine: [0.45, 0, 0],
            shoulderL: [-1.0, 0, 0.3],
            elbowL: [-0.5, 0, 0],
            shoulderR: [-1.1, 0, -0.1],
            elbowR: [-0.2, 0, 0],
            handR: [2.6, 0, 0],
            chest: [0.2, 0, 0],
          },
          rootY: -0.42,
        },
      },
      {
        t: 0.84,
        pose: {
          joints: {
            thighL: [-0.85, 0, 0.25],
            kneeL: [1.4, 0, 0],
            footL: [-0.5, 0, 0],
            thighR: [0.2, 0, -0.25],
            kneeR: [1.5, 0, 0],
            footR: [-0.8, 0, 0],
            spine: [0.4, 0, 0],
            shoulderL: [-0.9, 0, 0.3],
            shoulderR: [-1.1, 0, -0.1],
            elbowR: [-0.2, 0, 0],
            handR: [2.6, 0, 0],
            chest: [0.2, 0, 0],
          },
          rootY: -0.38,
        },
      },
      {
        t: 1,
        pose: { joints: { ...STANCE, shoulderR: [-0.5, 0, -0.1], elbowR: [-0.6, 0, 0] }, rootY: -0.05 },
      },
    ],
  },
  ATTACK_HEAVY: {
    duration: 1.5,
    keys: [
      {
        t: 0,
        pose: { joints: { ...STANCE, shoulderR: [-1.0, 0, -0.3], elbowR: [-0.8, 0, 0] }, rootY: -0.03 },
      },
      {
        t: 0.55,
        pose: {
          joints: {
            ...STANCE,
            spine: [-0.3, 0, 0],
            chest: [-0.2, 0, 0],
            shoulderR: [-3.0, 0, -0.2],
            elbowR: [-0.7, 0, 0],
            handR: [0.4, 0, 0],
            shoulderL: [-2.8, 0, 0.35],
            elbowL: [-0.8, 0, 0],
          },
          rootY: 0.02,
        },
      },
      {
        t: 0.64,
        pose: {
          joints: {
            ...STANCE,
            spine: [-0.32, 0, 0],
            chest: [-0.22, 0, 0],
            shoulderR: [-3.1, 0, -0.2],
            elbowR: [-0.75, 0, 0],
            handR: [0.45, 0, 0],
            shoulderL: [-2.9, 0, 0.35],
            elbowL: [-0.85, 0, 0],
          },
          rootY: 0.03,
        },
      },
      {
        t: 0.7,
        pose: {
          joints: {
            ...STANCE,
            thighL: [-0.7, 0, 0.15],
            kneeL: [0.9, 0, 0],
            spine: [0.55, 0, 0],
            chest: [0.2, 0, 0],
            shoulderR: [-0.6, 0, -0.1],
            elbowR: [-0.05, 0, 0],
            handR: [0.2, 0, 0],
            shoulderL: [-0.6, 0, 0.2],
            elbowL: [-0.4, 0, 0],
          },
          rootY: -0.22,
          rootZ: 0.25,
        },
      },
      {
        t: 1,
        pose: {
          joints: { ...STANCE, shoulderR: [-0.5, 0, -0.2], elbowR: [-0.6, 0, 0] },
          rootY: -0.04,
          rootZ: 0.1,
        },
      },
    ],
  },
  DODGE: {
    duration: 0.3,
    keys: [
      {
        t: 0,
        pose: {
          joints: {
            spine: [0.5, 0, 0],
            chest: [0.1, 0, 0],
            shoulderL: [1.1, 0, 0.5],
            shoulderR: [1.1, 0, -0.5],
            elbowL: [-0.2, 0, 0],
            elbowR: [-0.2, 0, 0],
            thighL: [-0.9, 0, 0],
            kneeL: [1.2, 0, 0],
            thighR: [0.6, 0, 0],
            kneeR: [0.6, 0, 0],
            skirt: [-0.4, 0, 0],
            hairTail: [1.1, 0, 0],
          },
          rootY: -0.1,
        },
      },
      {
        t: 0.7,
        pose: {
          joints: {
            spine: [0.45, 0, 0],
            shoulderL: [1.2, 0, 0.6],
            shoulderR: [1.2, 0, -0.6],
            thighL: [0.5, 0, 0],
            kneeL: [0.4, 0, 0],
            thighR: [-0.8, 0, 0],
            kneeR: [1.1, 0, 0],
            skirt: [-0.35, 0, 0],
            hairTail: [1.0, 0, 0],
          },
          rootY: 0.05,
        },
      },
      {
        t: 1,
        pose: {
          joints: { spine: [0.2, 0, 0], shoulderL: [0.4, 0, 0.3], shoulderR: [0.2, 0, -0.3] },
          rootY: 0,
        },
      },
    ],
  },
  DEATH: {
    duration: 1.2,
    hold: true,
    keys: [
      { t: 0, pose: { joints: { spine: [0.3, 0, 0], head: [0.3, 0, 0] }, rootY: 0 } },
      {
        t: 0.3,
        pose: {
          joints: {
            spine: [0.5, 0, 0.1],
            head: [0.4, 0, 0],
            thighL: [-1.2, 0, 0],
            kneeL: [2.0, 0, 0],
            thighR: [-1.0, 0, 0],
            kneeR: [2.1, 0, 0],
            shoulderL: [0.2, 0, 0.3],
            shoulderR: [0.2, 0, -0.3],
          },
          rootY: -0.42,
        },
      },
      {
        t: 0.75,
        pose: {
          joints: {
            spine: [0.1, 0, 0.1],
            head: [-0.2, 0.3, 0],
            thighL: [-0.6, 0, 0.1],
            kneeL: [0.9, 0, 0],
            thighR: [-0.3, 0, -0.1],
            kneeR: [0.6, 0, 0],
            shoulderL: [-0.6, 0, 1.2],
            shoulderR: [-0.4, 0, -1.3],
            elbowL: [-0.4, 0, 0],
            elbowR: [-0.3, 0, 0],
          },
          rootY: -0.82,
          rootPitch: -1.45,
        },
      },
      {
        t: 1,
        pose: {
          joints: {
            spine: [0.05, 0, 0.1],
            head: [-0.25, 0.35, 0],
            thighL: [-0.5, 0, 0.1],
            kneeL: [0.7, 0, 0],
            thighR: [-0.2, 0, -0.1],
            kneeR: [0.4, 0, 0],
            shoulderL: [-0.6, 0, 1.3],
            shoulderR: [-0.4, 0, -1.4],
            elbowL: [-0.3, 0, 0],
            elbowR: [-0.2, 0, 0],
          },
          rootY: -0.84,
          rootPitch: -1.52,
        },
      },
    ],
  },
  REVIVE: {
    duration: 0.9,
    keys: [
      {
        t: 0,
        pose: {
          joints: {
            thighL: [-1.2, 0, 0],
            kneeL: [2.0, 0, 0],
            thighR: [-1.0, 0, 0],
            kneeR: [2.1, 0, 0],
            spine: [0.5, 0, 0],
          },
          rootY: -0.42,
        },
      },
      {
        t: 0.6,
        pose: {
          joints: { spine: [-0.1, 0, 0], shoulderL: [-0.8, 0, 1.0], shoulderR: [-0.8, 0, -1.0] },
          rootY: 0.05,
        },
      },
      { t: 1, pose: { joints: {}, rootY: 0 } },
    ],
  },
  VICTORY: {
    duration: 1.4,
    keys: [
      { t: 0, pose: { joints: { shoulderR: [-0.5, 0, -0.2] } } },
      {
        t: 0.4,
        pose: {
          joints: {
            shoulderR: [-2.9, 0, -0.25],
            elbowR: [-0.2, 0, 0],
            handR: [2.6, 0, 0],
            shoulderL: [0.2, 0, 0.4],
            spine: [-0.15, 0, 0],
          },
          rootY: 0.02,
        },
      },
      {
        t: 0.8,
        pose: {
          joints: {
            shoulderR: [-2.9, 0, -0.25],
            elbowR: [-0.2, 0, 0],
            handR: [2.6, 0, 0],
            shoulderL: [0.2, 0, 0.4],
            spine: [-0.15, 0, 0],
          },
          rootY: 0.02,
        },
      },
      { t: 1, pose: { joints: {} } },
    ],
  },
};

/** Short additive flinch for TAKE_DAMAGE. */
export const FLINCH: Pose = {
  joints: {
    spine: [-0.18, 0, 0.06],
    chest: [-0.12, 0, -0.05],
    head: [-0.25, 0, 0.1],
    shoulderL: [0.3, 0, 0.2],
  },
};

export function stunPose(t: number): Pose {
  return {
    joints: {
      spine: [0.35, 0, Math.sin(t * 7) * 0.12],
      head: [0.3, Math.sin(t * 5) * 0.3, Math.sin(t * 7) * 0.2],
      shoulderL: [0.3, 0, 0.1],
      shoulderR: [0.3, 0, -0.1],
      elbowL: [-0.1, 0, 0],
      elbowR: [-0.1, 0, 0],
      kneeL: [0.4, 0, 0],
      kneeR: [0.45, 0, 0],
      thighL: [-0.2, 0, 0],
      thighR: [-0.2, 0, 0],
    },
    rootY: -0.08,
  };
}
